import { useEffect, useRef, useState } from "react";

import ThumbImg from "../../assets/images/conversations/002.webp";
import PlayIcon  from "../../assets/icons/conversations/play-icon.svg";

/* ─────────────────────────────────────────────
   PREFIX: sl-
───────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Roboto:wght@400;500&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  .sl-page {
    background: #1A1A1A;
    font-family: 'Roboto', sans-serif;
    color: #fff;
  }
  .sl-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
  }

  .sl-hero { padding-top: 60px; padding-bottom: 60px; }
  .sl-hero__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    align-items: start;
  }
  .sl-hero__left { display: flex; flex-direction: column; gap: 44px; }
  .sl-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .sl-hero__title.sl-visible { opacity: 1; transform: translateY(0); }
  .sl-hero__spotify {
    border-radius: 12px;
    overflow: hidden;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.18s, transform 0.7s ease 0.18s;
  }
  .sl-hero__spotify.sl-visible { opacity: 1; transform: translateY(0); }
  .sl-hero__spotify iframe {
    display: block; width: 100%; height: 152px; border: none; border-radius: 12px;
  }
  .sl-hero__right {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s;
  }
  .sl-hero__right.sl-visible { opacity: 1; transform: translateY(0); }

  .sl-yt-thumb {
    position: relative; width: 100%; border-radius: 12px;
    overflow: hidden; cursor: pointer; display: block; background: #000;
  }
  .sl-yt-thumb img {
    width: 100%; height: auto; display: block;
    border-radius: 12px; transition: opacity 0.3s ease;
  }
  .sl-yt-thumb:hover img { opacity: 0.85; }
  .sl-yt-thumb__play {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    background: none; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
  }
  .sl-yt-thumb__play img { width: 40px; height: 44px; }
  .sl-yt-iframe-wrap {
    position: relative; width: 100%; padding-bottom: 56.25%;
    height: 0; border-radius: 12px; overflow: hidden;
  }
  .sl-yt-iframe-wrap iframe {
    position: absolute; top: 0; left: 0;
    width: 100%; height: 100%; border: none;
  }

  .sl-body {
    padding-bottom: 100px; opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .sl-body.sl-visible { opacity: 1; transform: translateY(0); }
  .sl-body__para {
    font-family: 'Roboto', sans-serif; font-size: 16px;
    font-weight: 400; color: #E2E2E2; line-height: 160%; margin-bottom: 14px;
  }
  .sl-body__para:last-child { margin-bottom: 0; }

  /* ── TABLET (max 768px) ── */
  @media (max-width: 768px) {
    .sl-inner { padding-left: 32px; padding-right: 32px; }
    .sl-hero { padding-top: 32px; padding-bottom: 48px; }
    .sl-hero__grid { grid-template-columns: 1fr 1fr; gap: 24px; }
    .sl-hero__title { font-size: 24px; }
    .sl-hero__left { gap: 16px; }
    .sl-body__para { font-size: 14px; margin-bottom: 12px; }
    .sl-body { padding-bottom: 48px; }
    .sl-yt-thumb__play img { width: 30px; height: 32px; }
  }

  /* ── MOBILE (max 480px) ── */
  @media (max-width: 480px) {
    .sl-inner { padding-left: 16px; padding-right: 16px; }
    .sl-hero { padding-top: 51px; padding-bottom: 30px; }
    .sl-hero__grid { grid-template-columns: 1fr; gap: 30px; }
    .sl-hero__left { gap: 30px; }
    .sl-hero__title { font-size: 30px; }
    .sl-body__para { font-size: 16px; margin-bottom: 10px; }
    .sl-body { padding-bottom: 30px; }
    .sl-yt-thumb__play img { width: 30px; height: 32px; }
  }
`;

function useFadeUp(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("sl-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
}

const YT_VIDEO_ID = "REPLACE_EP02_VIDEO_ID";

function YouTubeEmbed() {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <div className="sl-yt-iframe-wrap">
        <iframe
          src={`https://www.youtube.com/embed/${YT_VIDEO_ID}?autoplay=1&rel=0`}
          title="EP 02 Stefan Louis"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <div className="sl-yt-thumb" onClick={() => setPlaying(true)} role="button" aria-label="Play video">
      <img src={ThumbImg} alt="EP 02 Stefan Louis thumbnail" />
      <div className="sl-yt-thumb__play">
        <img src={PlayIcon} alt="Play" />
      </div>
    </div>
  );
}

function HeroSection() {
  const titleRef   = useRef(null);
  const spotifyRef = useRef(null);
  const rightRef   = useRef(null);
  useFadeUp(titleRef);
  useFadeUp(spotifyRef);
  useFadeUp(rightRef);

  return (
    <section className="sl-hero">
      <div className="sl-inner">
        <div className="sl-hero__grid">
          <div className="sl-hero__left">
            <h1 ref={titleRef} className="sl-hero__title">
              EP 02 | Candid conversation with Stefan LOUIS, CEO of Nexcharge on the evolution of lithium industry in India
            </h1>
            <div ref={spotifyRef} className="sl-hero__spotify">
              <iframe
                src="https://open.spotify.com/embed/episode/7iLsSRZlZ9WVko2CuilxPr?utm_source=generator&theme=0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="EP 02 Spotify Player"
              />
            </div>
          </div>
          <div ref={rightRef} className="sl-hero__right">
            <YouTubeEmbed />
          </div>
        </div>
      </div>
    </section>
  );
}

function BodySection() {
  const ref = useRef(null);
  useFadeUp(ref);
  return (
    <section ref={ref} className="sl-body">
      <div className="sl-inner">
        <p className="sl-body__para">
          Stefan LOUIS, CEO of Nexcharge speaks with Ravin MIRCHADANI on the evolution of the lithium industry in India. Nexcharge is a joint venture between Leclanché of Switzerland and Exide Industries of India and is focused on manufacturing the latest technology lithium batteries in India. Stefan covers his experience establishing a factory in India, an analysis of government policy on fostering EV growth, and attracting lithium battery manufacturing to India. He covers a deep analysis on what India needs to do in order to catch up with China on lithium and EVs, how the sector will evolve in India based on the application use case differences in India versus global benchmarks, and also the reality of building an industry in India based on a supply chain that is presently dominated by China. Tune-In to the Energizing India podcast to hear the opinions of key opinion leaders on lithium-based energy storage.
        </p>
      </div>
    </section>
  );
}

export default function Ep02StefanLouis() {
  return (
    <>
      <style>{CSS}</style>
      <div className="sl-page">
        <HeroSection />
        <BodySection />
      </div>
    </>
  );
}